import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";

import type { TFetchStatus, TLocation, TParamsPagination, TStock } from "./types";

export const fetchStocks = createAsyncThunk(
    "stock/getStocks",
    async (options: TParamsPagination, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/stock", { params: options });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const putStock = createAsyncThunk(
    "stock/putStock",
    async (
        { titleId, data, activePeriod }: { titleId: number; data: Partial<TStock>; activePeriod?: number },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(`${import.meta.env.REACT_APP_API_ENDPOINT}/title/${titleId}/stock`, data, {
                params: { periodId: activePeriod },
            });
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchLocations = createAsyncThunk("stock/getLocations", async (periodId: number, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/stock/location", {
            params: { period: periodId },
        });
        return response.data.data.locations;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const postLocations = createAsyncThunk(
    "stock/postLocations",
    async (locations: string[], { rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/stock/location", {
                name: locations,
            });
            return { ids: response.data.data.locationsId, locations };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const putLocations = createAsyncThunk(
    "stock/putLocations",
    async ({ locationId, locationName }: { locationId: any[]; locationName: string }, { rejectWithValue }) => {
        try {
            await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + `/stock/location/${locationId}`, {
                name: locationName,
            });
            return { locationId, locationName };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const deleteLocation = createAsyncThunk(
    "stock/deleteLocation",
    async (locationId: number, { rejectWithValue }) => {
        try {
            const response = await axios.delete(
                import.meta.env.REACT_APP_API_ENDPOINT + `/stock/location/${locationId}`,
            );
            return response.data.result;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

interface IStateStore {
    stockStatus: TFetchStatus;
    locationStatus: TFetchStatus;
    stocks: TStock[];
    locations: TLocation[];
}

const initialState: IStateStore = {
    stockStatus: "idle",
    locationStatus: "idle",
    stocks: [],
    locations: [],
};

export const stockSlice = createSlice({
    name: "stock",
    initialState,
    reducers: {
        setStockStatus(state, action) {
            state.stockStatus = action.payload;
        },
        setLocationStatus(state, action) {
            state.locationStatus = action.payload;
        },
        setStock(state, action) {
            state.stocks = action.payload;
        },
        setLocations(state, action) {
            state.locations = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchStocks.fulfilled, (state, action) => {
            state.stocks = action.payload.entries;
            state.stockStatus = "succeeded";
        });
        builder.addCase(fetchStocks.pending, (state) => {
            state.stocks = [];
            state.stockStatus = "pending";
        });
        builder.addCase(fetchStocks.rejected, (state, { payload }) => {
            state.stockStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(putStock.fulfilled, (state, action) => {
            state.stocks = state.stocks.map((e) => {
                e.id === action.payload.id && (e = { ...e, ...action.payload });
                return e;
            });
            toast.success(`${t("Management.StockUpdateSuccess")}`);
            state.stockStatus = "succeeded";
        });
        builder.addCase(putStock.pending, (state) => {
            state.stockStatus = "pending";
        });
        builder.addCase(putStock.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.stockStatus = "failed";
        });

        builder.addCase(fetchLocations.fulfilled, (state, action) => {
            state.locationStatus = "succeeded";
            state.locations = action.payload;
        });

        builder.addCase(fetchLocations.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.locationStatus = "failed";
        });

        builder.addCase(postLocations.fulfilled, (state, action) => {
            toast.success(`${t("Management.CreateLocationSuccess")}`);
            const newStockLocations = action.payload.ids.map((item: any, i: number) => ({
                id: item,
                name: action.payload.locations[i],
            }));
            const currentLocations = JSON.parse(JSON.stringify(state.locations));
            state.locations = [...currentLocations, ...newStockLocations];
        });
        builder.addCase(postLocations.rejected, () => {
            toast.error(`${t("Management.CreateLocationError")}`);
        });

        builder.addCase(putLocations.fulfilled, (state, action) => {
            toast.success(`${t("Management.UpdateLocationSuccess")}`);
            const currentLocations = JSON.parse(JSON.stringify(state.locations));
            const newId = currentLocations.findIndex((item: any) => item.id == action.payload.locationId);
            currentLocations[newId] = {
                ...currentLocations[newId],
                name: action.payload.locationName,
            };
            state.locations = currentLocations;
        });
        builder.addCase(putLocations.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(deleteLocation.fulfilled, () => {
            toast.success(`${t("Management.DeleteLocationSuccess")}`);
        });
        builder.addCase(deleteLocation.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
    },
});

export const { setStockStatus, setStock } = stockSlice.actions;

export default stockSlice.reducer;
